import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { serversApi } from '@/lib/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'

interface PromptsTabProps {
  serverId: string
  serverName?: string
}

export function PromptsTab({ serverId, serverName }: PromptsTabProps) {
  const queryClient = useQueryClient()
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['serverPrompts', serverId],
    queryFn: async () => await serversApi.listPrompts(serverId),
    enabled: !!serverId,
  })

  const prompts = Array.isArray((data as any)?.prompts) ? (data as any).prompts : []

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [instructions, setInstructions] = useState('')
  const [argumentsJSON, setArgumentsJSON] = useState('{"topic": {"type": "string", "description": "Topic"}}')

  const addMutation = useMutation({
    mutationFn: async () => {
      if (!name) throw new Error('Name is required')
      let args: Record<string, any> | undefined
      try {
        args = argumentsJSON ? JSON.parse(argumentsJSON) : undefined
      } catch (e) {
        throw new Error('Arguments must be valid JSON')
      }
      return await serversApi.addPrompt(serverId, {
        name,
        description: description || undefined,
        arguments: args,
        instructions: instructions || undefined,
      })
    },
    onSuccess: () => {
      toast.success('Prompt saved')
      queryClient.invalidateQueries({ queryKey: ['serverPrompts', serverId] })
      setName('')
      setDescription('')
      setInstructions('')
      setArgumentsJSON('')
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to save prompt'),
  })

  const removeMutation = useMutation({
    mutationFn: async (promptName: string) => await serversApi.removePrompt(serverId, promptName),
    onSuccess: () => {
      toast.success('Prompt removed')
      queryClient.invalidateQueries({ queryKey: ['serverPrompts', serverId] })
    },
    onError: () => toast.error('Failed to remove prompt'),
  })

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Prompts</CardTitle>
          <CardDescription>List of MCP prompts defined for {serverName || 'this server'}</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground">Loading prompts...</p>
          ) : error ? (
            <div className="flex items-center justify-between">
              <p className="text-red-500">Failed to load prompts</p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
            </div>
          ) : prompts.length === 0 ? (
            <p className="text-muted-foreground">No prompts yet.</p>
          ) : (
            <div className="space-y-3">
              {prompts.map((p: any, idx: number) => (
                <div key={idx} className="flex items-start justify-between border rounded p-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">Prompt</Badge>
                      <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">{p.name}</code>
                    </div>
                    <div className="text-sm font-medium">{p.description || 'No description'}</div>
                    {p.arguments && (
                      <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-40">
                        <code>{JSON.stringify(p.arguments, null, 2)}</code>
                      </pre>
                    )}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => removeMutation.mutate(p.name)}
                    disabled={removeMutation.isPending}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Add or Update Prompt</CardTitle>
          <CardDescription>Creating with the same name will update the prompt</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm">Name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="quick_help" />
            </div>
            <div>
              <label className="text-sm">Description</label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short help" />
            </div>
          </div>
          <div>
            <label className="text-sm">Arguments (JSON)</label>
            <Textarea value={argumentsJSON} onChange={(e) => setArgumentsJSON(e.target.value)} placeholder='{"topic": {"type": "string"}}' rows={5} />
          </div>
          <div>
            <label className="text-sm">Instructions</label>
            <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Provide help about {topic}." rows={5} />
          </div>
          <div className="flex justify-end">
            <Button onClick={() => addMutation.mutate()} disabled={addMutation.isPending || !name}>
              Save Prompt
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}


